import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveBlogSeo } from '../../../Apis/touzenBlogSeoApi';
import { useBlogReleasePolicy } from './BlogReleasePolicy';

let nextOwner = 0;
const origin = 'https://www.touzen.jp';
const fallback = `${origin}/touzen/blog-default-share.jpg`;
const productionHost = () => ['touzen.jp', 'www.touzen.jp'].includes(window.location.hostname.toLowerCase());
const yowan = () => window.__NODE_V11_DOMAIN_APP__ === 'yowan' || /(^|\.)yowan\.jp$/.test(window.location.hostname)
  || ['yowan-stg.shimashou.com', 'yowan-prod.shimashou.com'].includes(window.location.hostname)
  || (['localhost', '127.0.0.1'].includes(window.location.hostname) && new URLSearchParams(window.location.search).get('domain') === 'yowan');

export default function BlogSeoHead({ ready = false }) {
  const { public_release: released } = useBlogReleasePolicy();
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const query = new URLSearchParams(location.search);
  const pages = query.getAll('page');
  const derived = path === '/blog' && (query.has('author') || query.has('tag') || (pages.length > 0 && (pages.length !== 1 || pages[0] !== '1')));
  useEffect(() => {
    if (yowan()) return undefined;
    const owner = String(++nextOwner);
    const controller = new AbortController();
    let active = true;
    const changes = new Map();
    const own = (node) => {
      if (!changes.has(node)) changes.set(node, { original: node.parentNode ? node.cloneNode(true) : null, last: '' });
      node.setAttribute('data-touzen-blog-seo', owner);
      if (!node.parentNode) document.head.appendChild(node);
      return node;
    };
    const remember = (node) => { changes.get(node).last = node.outerHTML; };
    const meta = (attribute, key, value) => {
      const nodes = [...document.head.querySelectorAll(`meta[${attribute}="${key}"]`)];
      if (!nodes.length) nodes.push(document.createElement('meta'));
      nodes.forEach((node) => { own(node); node.setAttribute(attribute, key); node.content = String(value); remember(node); });
    };
    const title = own(document.head.querySelector('title') || document.createElement('title'));
    title.textContent = '刀禅ブログ | 刀禅'; remember(title);
    const previousCanonical = [...document.head.querySelectorAll('link[rel="canonical"]')];
    previousCanonical.forEach((node) => node.remove());
    meta('name', 'description', '刀禅の公開記事を掲載しています。');
    meta('name', 'robots', released && derived && productionHost() ? 'noindex,follow' : 'noindex,nofollow');
    meta('property', 'og:type', 'website'); meta('property', 'og:title', '刀禅ブログ | 刀禅');
    meta('property', 'og:description', '刀禅の公開記事を掲載しています。'); meta('property', 'og:url', '');
    meta('property', 'og:site_name', '刀禅'); meta('property', 'og:image', fallback);
    meta('property', 'og:image:alt', '刀禅ブログの共通ロゴ'); meta('name', 'twitter:image:alt', '刀禅ブログの共通ロゴ');
    meta('property', 'og:image:width', 1200); meta('property', 'og:image:height', 630); meta('property', 'og:image:type', 'image/jpeg');
    meta('name', 'twitter:card', 'summary_large_image'); meta('name', 'twitter:title', '刀禅ブログ | 刀禅');
    meta('name', 'twitter:description', '刀禅の公開記事を掲載しています。'); meta('name', 'twitter:url', ''); meta('name', 'twitter:image', fallback);
    const current = () => active && !controller.signal.aborted && !yowan() && (window.location.pathname.replace(/\/+$/, '') || '/') === path;
    if (released && ready && !derived && productionHost()) {
      resolveBlogSeo(path, controller.signal).then((route) => {
        if (!current()) return;
        title.textContent = route.seo_title; remember(title);
        meta('name', 'description', route.seo_description); meta('name', 'robots', 'index,follow');
        const canonical = own(document.createElement('link')); canonical.rel = 'canonical'; canonical.href = route.canonical_url; remember(canonical);
        meta('property', 'og:type', path === '/blog' ? 'website' : 'article');
        meta('property', 'og:image:alt', route.image_alt); meta('name', 'twitter:image:alt', route.image_alt);
        for (const [key, value] of Object.entries({ title: route.seo_title, description: route.seo_description, url: route.canonical_url, image: route.image_profiles.share.url })) {
          meta('property', `og:${key}`, value); meta('name', `twitter:${key}`, value);
        }
        const entity = { '@context': 'https://schema.org', '@type': route.schema_type, name: route.seo_title, description: route.seo_description, url: route.canonical_url };
        if (path === '/blog') {
          entity.mainEntity = { '@type': 'ItemList', itemListElement: route.collection_items.map((item, index) => ({ '@type': 'ListItem', position: index + 1,
            item: { '@type': 'BlogPosting', headline: item.title, url: item.url, description: item.description,
              ...(item.author_name.trim() ? { author: { '@type': 'Person', name: item.author_name } } : {}), ...(item.published_at ? { datePublished: item.published_at } : {}) } })) };
        } else {
          Object.assign(entity, { headline: route.seo_title, articleBody: route.body, keywords: route.keywords });
          const searchNames = { search_wide: 'wide', search_landscape: 'landscape', search_square: 'square' };
          const articleImages = Object.entries(searchNames).map(([name, suffix]) => ({ url: route.image_profiles[name].url,
            fallbackUrl: `${origin}/touzen/blog-default-search-${suffix}.jpg` })).filter((image) => image.url !== image.fallbackUrl).map((image) => image.url);
          if (!articleImages.length && route.image_profiles.share.url !== fallback) articleImages.push(route.image_profiles.share.url);
          if (articleImages.length) entity.image = articleImages;
          if (route.author_name.trim()) entity.author = { '@type': 'Person', name: route.author_name, ...(route.author_url ? { url: route.author_url } : {}) };
          if (route.published_at) entity.datePublished = route.published_at;
          if (route.modified_at) entity.dateModified = route.modified_at;
        }
        const script = own(document.createElement('script')); script.type = 'application/ld+json';
        script.textContent = JSON.stringify(entity).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026'); remember(script);
      }).catch(() => { /* Keep the pending noindex state; no retry or stale metadata. */ });
    }
    return () => {
      active = false; controller.abort();
      changes.forEach(({ original, last }, node) => {
        // Restore only a value still owned by this mount; preserve later writers.
        if (node.getAttribute('data-touzen-blog-seo') !== owner) return;
        if (node.outerHTML === last) { if (original) node.replaceWith(original); else node.remove(); }
        else node.removeAttribute('data-touzen-blog-seo');
      });
      if (!document.head.querySelector('link[rel="canonical"]')) previousCanonical.forEach((node) => document.head.appendChild(node));
    };
  }, [path, derived, ready, released]);
  return null;
}
